import React from 'react';
import { Container, Card, CardContent, Button, Stepper, Step, StepLabel } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Page from 'src/components/Page';
import { useHistory } from 'react-router-dom';
import ProductSelect from './ProductSelect';
import CustomerInfo from './CustomerInfo';
import PaymentInfo from './PaymentInfo';
import { useDispatch, useSelector } from 'react-redux';
import { cuteStarActions } from 'src/store/slices/cuteStarSlice';
import { paymentActions, paymentThunks } from 'src/store/slices/paymentSlice';

const useStyles = makeStyles(theme => ({
  root: { paddingTop: theme.spacing(6), paddingBottom: theme.spacing(6) },
  card: { padding: theme.spacing(2) },
  header: { fontWeight: 600, marginBottom: theme.spacing(2) },
  backBtn: { marginTop: theme.spacing(3) },
  stepActions: {
    marginTop: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative'
  },
  skuRow: { display: 'flex', gap: theme.spacing(1), alignItems: 'center', marginTop: theme.spacing(2) },
  grow: { flex: 1 },
  resultList: { display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 },
  productCard: { padding: 12, border: '1px solid #d9e2ec', background: '#ffffff', color: '#1f2d3a', borderRadius: 12, transition: 'box-shadow .25s ease, border-color .25s ease', cursor: 'pointer', '&:hover': { boxShadow: '0 6px 18px -6px rgba(0,0,0,0.12)', borderColor: '#b5c6d6' } },
  selected: { borderColor: '#1976d2 !important', background: '#f3f9ff', boxShadow: '0 0 0 2px rgba(25,118,210,.25)' },
  productHeaderRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  linkBtn: { textTransform: 'none', alignSelf: 'flex-start', fontSize: 12, padding: '4px 10px', background: 'rgba(255,255,255,0.06)' },
  sectionTitle: { fontWeight: 600, letterSpacing: .3 },
  miniSummary: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 },
  chip: { background: '#13293f', border: '1px solid #1e4266', padding: '5px 12px', borderRadius: 16, fontSize: 12, fontWeight: 500, color: '#e2edf5' },
  divider: { margin: '28px 0 10px', height: 1, background: 'linear-gradient(90deg,#1c3d63, transparent)' },
  priceBadgeWrap: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 },
  priceBadge: { background: '#0d47a1', color: '#ffffff', padding: '10px 18px 14px', borderRadius: 18, display: 'flex', flexDirection: 'column', lineHeight: 1.05, boxShadow: '0 4px 18px -4px rgba(13,71,161,.45)' },
  priceValue: { fontSize: '1.9rem', fontWeight: 700, letterSpacing: '.5px', fontVariantNumeric: 'tabular-nums', color: '#ffffff' },
  priceCur: { fontSize: 11, fontWeight: 600, letterSpacing: '.55px', textTransform: 'uppercase', opacity: .9, position: 'relative', top: -2, color: '#bbdefb' },
  priceComment: { fontSize: 10, letterSpacing: '.5px', opacity: .6, marginTop: 4, fontWeight: 500, color: '#e3f2fd' },
  richPricePanel: { marginTop: 20, padding: '26px 28px 30px', border: '1px solid #16395c', borderRadius: 22, background: '#0f2237', position: 'relative', overflow: 'hidden' },
  richPriceGlow: { position: 'absolute', inset: 0, background: 'radial-gradient(circle at 75% 20%,rgba(25,118,210,.32),rgba(25,118,210,0) 65%)', pointerEvents: 'none' },
  richPriceLabel: { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.65px', color: '#64b5f6', marginBottom: 10 },
  richPriceBig: { fontSize: '2.8rem', fontWeight: 700, lineHeight: 1, color: '#ffffff', letterSpacing: '.5px' },
  richSku: { fontSize: 12, fontWeight: 500, color: '#b0c4d4', marginTop: 10 },
  minimalSummaryWrap: { marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: 8 },
  minimalBadge: { fontSize: 11, padding: '4px 10px', borderRadius: 12, background: '#f1f5f9', border: '1px solid #d8e2ec', fontWeight: 600, letterSpacing: '.4px', color: '#1f2d3a' },
  minimalLinkBtn: { textTransform: 'none', fontSize: 11, padding: '4px 10px' }
}));

const PRODUCT_LINK = "https://www.beko.com/ro-ro/produse/masini-de-spalat-rufe/masina-de-spalat-rufe-8-kg-1400-rpm-b5wf-u78418-wb";
const CURRENCY_LABEL = 'RON';

const ProductSales = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = React.useState(0);
  const productName = useSelector(s => s.payment.productName);
  const sku = useSelector(s => s.payment.productSKU);
  const productId = useSelector(s => s.payment.productId); // added
  const price = useSelector(s => s.payment.productPrice);
  const workOrderId = useSelector(s => s.payment.workOrderId);
  const cuiNumber = useSelector(s => s.payment.cuiNumber); // added
  const paymentInitiated = useSelector(s => s.payment.paymentInitiated);
  const verifiedCustomerId = useSelector(s => s.payment.verifiedCustomerId);
  // New: use createdProductOrderId for ApplicationReference
  const createdProductOrderId = useSelector(s => s.payment.createdProductOrderId);
  // New: POS device selection from Redux
  const selectedPosDeviceId = useSelector(s => s.payment.selectedPosDeviceId);
  const employeePosMatching = useSelector(s => s.payment.employeePosMatching) || [];
  // New: product pre-sale error message
  const [preSaleError, setPreSaleError] = React.useState('');
  // Track invalid fields for CustomerInfo highlighting
  const [invalidFields, setInvalidFields] = React.useState([]);
  const [customerInfoValid, setCustomerInfoValid] = React.useState(false);
  const [customerName, setCustomerName] = React.useState('');
  const [customerSurname, setCustomerSurname] = React.useState('');
  const [customerPhone, setCustomerPhone] = React.useState('');
  const [customerEmail, setCustomerEmail] = React.useState('');
  const [city, setCity] = React.useState('');
  const [street, setStreet] = React.useState('');
  const [postCode, setPostCode] = React.useState('');
  const [addressFree, setAddressFree] = React.useState('');
  const [proformaGenerated, setProformaGenerated] = React.useState(false);

  const steps = ['Product Select', 'Customer Info', 'Payment Info'];

  const canNext = () => {
    if (activeStep === 0) return !!sku && !!price;
    if (activeStep === 1) {
      if (workOrderId || verifiedCustomerId) return true;
      return customerInfoValid;
    }
    if (activeStep === 2) return true;
    return false;
  };

  const validateCustomerFields = () => {
    const invalid = [];
    if (!customerName.trim()) invalid.push('Name');
    if (!customerSurname.trim()) invalid.push('Surname');
    if (!customerPhone.trim()) invalid.push('CustomerPhone');
    if (!city.trim()) invalid.push('City');
    if (!street.trim()) invalid.push('Street');
    if (!postCode.trim()) invalid.push('PostCode');
    if (!addressFree.trim()) invalid.push('Address');
    setInvalidFields(invalid);
    return invalid.length === 0;
  };

  const handleCreateCustomer = () => {
    // disable if consumer already selected or WO present
    if (verifiedCustomerId || workOrderId) return;
    if (!validateCustomerFields()) return;
    const dto = {
      Id: 0,
      Name: customerName.trim(),
      Surname: customerSurname.trim(),
      CustomerPhone: customerPhone.trim(),
      CustomerEmail: (customerEmail || '').trim(),
      Address: {
        Id: 0,
        City: city.trim(),
        Street: street.trim(),
        PostCode: postCode.trim(),
        Address: addressFree.trim()
      }
    };
    dispatch(paymentThunks.CreateCustomer(dto)).then(res => {
      const newId = res?.payload?.ResponseObject?.Id ?? res?.payload?.Id;
      if (newId) {
        dispatch(paymentActions.setVerifiedCustomerId(newId));
      }
    }).catch(() => {});
  };

  const handleNext = () => { if (activeStep < steps.length - 1) setActiveStep(s => s + 1); };
  const handleBack = () => setActiveStep(s => Math.max(0, s - 1));

  const handleProformaGenerated = () => {
    setProformaGenerated(true);
  };

  const handleFinish = () => {
    if (!sku || !price) {
      console.log('[ProceedToPayment] Missing sku/price. sku:', sku, 'price:', price);
      return;
    }
    setPreSaleError('');
    // Get selected POS device
    const selectedPos = employeePosMatching.find(p => p.Id === selectedPosDeviceId);
    const posUserRef = selectedPos?.PosMachineIdentifier || String(selectedPosDeviceId) || "john.doe";
    // Start payment flow
    const paymentSessionRequest = {
      ApplicationReference: String(createdProductOrderId ?? ''), // ensure string
      Amount: price,
      Currency: 'RON',
      UserRef: posUserRef,
      cuiNumber: cuiNumber || null,
      Products:[{
        Um:"pcs",
        Name: ((sku || '') + ' ' + (productName || '')).substring(0, 40),
        Price: price,
        VatId: "A",
        Quantity:"1",
        ProductId : productId || 0,
        ProductSku: sku || ''
      }]
    };
    console.log('[ProceedToPayment] Creating payment session...2', paymentSessionRequest);
    dispatch(paymentThunks.ProductPreSale(paymentSessionRequest))
      .then(resp => {
        // Error surface
        const payload = resp?.payload;
        if (payload && payload.error) {
          setPreSaleError(payload.error.Message || 'ProductPreSale failed.');
          return;
        }
        dispatch(paymentActions.setPaymentInitiated(true));
      })
      .catch(err => {
        setPreSaleError(err?.message || 'ProductPreSale exception.');
      });
  };

  return (
    <Page>
      <Container maxWidth="lg" className={classes.root}>
        <Card className={classes.card}>
          <CardContent>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map(label => (
                <Step key={label}><StepLabel>{label}</StepLabel></Step>
              ))}
            </Stepper>

            {activeStep === 0 && (
              <ProductSelect
                classes={classes}
                currency={CURRENCY_LABEL}
                productLink={PRODUCT_LINK}
              />
            )}
            {activeStep === 1 && (
              <CustomerInfo
                classes={classes}
                currency={CURRENCY_LABEL}
                productLink={PRODUCT_LINK}
                onValidityChange={setCustomerInfoValid}
                customerName={customerName} setCustomerName={setCustomerName}
                customerSurname={customerSurname} setCustomerSurname={setCustomerSurname}
                customerPhone={customerPhone} setCustomerPhone={setCustomerPhone}
                customerEmail={customerEmail} setCustomerEmail={setCustomerEmail}
                city={city} setCity={setCity}
                street={street} setStreet={setStreet}
                postCode={postCode} setPostCode={setPostCode}
                addressFree={addressFree} setAddressFree={setAddressFree}
                // New: invalid field list for error highlighting
                invalidFields={invalidFields}
              />
            )}
            {activeStep === 2 && (
              <PaymentInfo
                classes={classes}
                currency={CURRENCY_LABEL}
                productLink={PRODUCT_LINK}
                customerName={customerName}
                customerSurname={customerSurname}
                customerPhone={customerPhone}
                customerEmail={customerEmail}
                city={city}
                street={street}
                postCode={postCode}
                addressFree={addressFree}
                onProformaGenerated={handleProformaGenerated}
              />
            )}

            <div
              className={classes.stepActions}
            >
              <Button variant="outlined" size="small" disabled={activeStep === 0} onClick={handleBack}>Back</Button>
              {activeStep < steps.length - 1 ? (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleNext}
                    disabled={!canNext()}
                  >
                    Next
                  </Button>
                </>
              ) : (
                !paymentInitiated && (
                  <Button variant="contained" color="primary" size="small" onClick={handleFinish} disabled={!canNext() || !proformaGenerated}>
                    Proceed to Payment
                  </Button>
                )
              )}
            </div>
            {/* New: inline ProductPreSale error */}
            {preSaleError && (
              <div style={{ marginTop: 8 }}>
                <span style={{ color: '#d32f2f', fontSize: 12, fontWeight: 600 }}>
                  {preSaleError}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      </Container>
    </Page>
  );
};
export default ProductSales;
